/**
 * Cache utility for localStorage with TTL support
 */

const CACHE_PREFIX = 'kelkoo_cache_';
const DEFAULT_TTL = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

export const cacheUtils = {
  /**
   * Store data in cache with TTL
   */
  set: (key, data, ttl = DEFAULT_TTL) => {
    try {
      const cacheEntry = {
        data,
        timestamp: Date.now(),
        ttl
      };
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheEntry));
    } catch (error) {
      console.warn('Failed to set cache:', error);
      // Silently fail if localStorage is full or unavailable
    }
  },

  /**
   * Retrieve data from cache if not expired
   */
  get: (key) => {
    try {
      const cached = localStorage.getItem(CACHE_PREFIX + key);
      if (!cached) return null;

      const cacheEntry = JSON.parse(cached);
      const age = Date.now() - cacheEntry.timestamp;

      if (age < cacheEntry.ttl) {
        return cacheEntry.data;
      } else {
        // Expired, remove it
        localStorage.removeItem(CACHE_PREFIX + key);
        return null;
      }
    } catch (error) {
      console.warn('Failed to get cache:', error);
      return null;
    }
  },

  /**
   * Remove specific cache entry
   */
  remove: (key) => {
    try {
      localStorage.removeItem(CACHE_PREFIX + key);
    } catch (error) {
      console.warn('Failed to remove cache:', error);
    }
  },

  /**
   * Clear all cache entries
   */
  clearAll: () => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  },

  /**
   * Generate cache key from search parameters
   */
  generateSearchKey: (params) => {
    const { query, page, sort, filters, filterGreaterThanEqual } = params;
    const filterStr = filters ? JSON.stringify(filters) : '';
    return `search_${query}_${page}_${sort}_${filterStr}_${filterGreaterThanEqual || ''}`;
  }
};

/**
 * Debounce utility function
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};
